import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { AlertCircle, CheckCircle, FileText, Image as ImageIcon, Download } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export default function DeliveryDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [pallets, setPallets] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverConfirmed, setDriverConfirmed] = useState(false);
  const [supervisorVerifyConfirmed, setSupervisorVerifyConfirmed] = useState(false);
  const [showSupervisorVerifyForm, setShowSupervisorVerifyForm] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const deliveryRes = await api.get(`/deliveries/${id}`);
      const deliveryData = deliveryRes.data;
      
      // If driver role and delivery is already accepted, redirect to driver view
      if (user.role === 'Driver' && deliveryData.driver_accepted) {
        toast.info('This delivery has already been accepted');
        navigate('/');
        return;
      }
      
      setDelivery(deliveryData);

      if (deliveryData.customer_id) {
        const customerRes = await api.get(`/customers/${deliveryData.customer_id}`);
        setCustomer(customerRes.data);
      }

      if (deliveryData.vehicle_id) {
        const vehicleRes = await api.get(`/vehicles/${deliveryData.vehicle_id}`);
        setVehicle(vehicleRes.data);
      }

      const palletsRes = await api.get(`/deliveries/${id}/pallets`);
      setPallets(palletsRes.data);

      const photosRes = await api.get(`/deliveries/${id}/photos`);
      setPhotos(photosRes.data);
    } catch (error) {
      toast.error('Failed to load delivery details');
    } finally {
      setLoading(false);
    }
  };

  const handleDriverAccept = async () => {
    if (!driverConfirmed) {
      toast.error('Please confirm DVSA compliance');
      return;
    }

    try {
      // Driver compliance statement
      const complianceStatement = "I confirm that I have carried out the required vehicle and load checks in accordance with DVSA guidance. I am satisfied that the vehicle is within its permitted weight limits and that the load is secure, correctly distributed, and safe to transport. I accept responsibility for ensuring the vehicle is safe and legal to drive at the time of departure.";
      
      // Accept delivery - use logged-in user's full name
      await api.post(`/deliveries/${id}/accept`, {
        driver_name: user.full_name,
        confirmed: driverConfirmed,
        compliance_statement: complianceStatement
      });
      
      // Generate PDF
      await api.post(`/deliveries/${id}/generate-pdf?pdf_method=reportlab`);
      
      toast.success('Delivery accepted and PDF generated successfully!');
      
      // Redirect driver to their main page after acceptance
      if (user.role === 'Driver') {
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept delivery');
    }
  };

  const handleSupervisorVerify = async () => {
    if (!supervisorVerifyConfirmed) {
      toast.error('Please confirm supervisor verification');
      return;
    }

    try {
      const complianceStatement = "I confirm that I have verified the recorded pallet weights, checked the total vehicle payload against the vehicle's permitted weight limit, and ensured the load is safe, secure, and compliant with DVSA load-security and weight regulations before releasing the vehicle for departure.";
      
      await api.post(`/deliveries/${id}/supervisor-verify`, {
        supervisor_name: user.full_name,
        verified: supervisorVerifyConfirmed,
        compliance_statement: complianceStatement
      });

      toast.success('Delivery verified successfully! Waiting for driver acceptance.');
      
      // Refresh data
      fetchData();
      setSupervisorVerifyConfirmed(false);
      setShowSupervisorVerifyForm(false);
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify delivery');
    }
  };
  const handleGeneratePDF = async () => {
    try {
      const response = await api.post(`/deliveries/${id}/generate-pdf?pdf_method=reportlab`);
      toast.success('PDF generated and emailed successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  if (!delivery) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">Delivery not found</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900" data-testid="delivery-detail-title">
              Delivery Details
            </h1>
            <p className="text-slate-600 mt-2">Invoice: {delivery.invoice_number || 'N/A'}</p>
          </div>
          <div className="flex gap-2">
            {delivery.pdf_url && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files${delivery.pdf_url.replace('/uploads', '')}`} target="_blank" rel="noopener noreferrer" download>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </a>
            )}
            {user.role === 'Admin' && (
              <Button onClick={handleGeneratePDF} data-testid="generate-pdf-button">
                <FileText className="h-4 w-4 mr-2" />
                Generate PDF
              </Button>
            )}
          </div>
        </div>

        {/* Payload Status Banner */}
        <Card className={`border-l-4 ${
          delivery.payload_status === 'ACCEPTABLE'
            ? 'border-l-green-500 bg-green-50'
            : 'border-l-red-500 bg-red-50'
        }`}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              {delivery.payload_status === 'ACCEPTABLE' ? (
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
              )}
              <div className="flex-1">
                <h3 className={`text-lg font-bold mb-1 ${
                  delivery.payload_status === 'ACCEPTABLE' ? 'text-green-900' : 'text-red-900'
                }`}>
                  {delivery.payload_status}
                </h3>
                <p className={delivery.payload_status === 'ACCEPTABLE' ? 'text-green-700' : 'text-red-700'}>
                  {delivery.payload_message}
                </p>
                {delivery.over_by_kg > 0 && (
                  <p className="mt-2 font-semibold text-red-800">
                    Overweight by: {delivery.over_by_kg.toFixed(2)} kg
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer & Vehicle Info */}
          <Card>
            <CardHeader>
              <CardTitle>Delivery Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {customer && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Customer</h4>
                  <p className="text-sm text-slate-600">{customer.business_name}</p>
                  <p className="text-sm text-slate-600">{customer.delivery_address}</p>
                </div>
              )}
              {vehicle && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Vehicle</h4>
                  <p className="text-sm text-slate-600">Registration: {vehicle.registration}</p>
                  <p className="text-sm text-slate-600">Max Payload: {vehicle.max_payload_kg} kg</p>
                </div>
              )}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Payload Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-600">Total Weight:</span>
                    <p className="font-medium">{delivery.total_pallet_weight_kg} kg</p>
                  </div>
                  <div>
                    <span className="text-slate-600">Vehicle Payload:</span>
                    <p className="font-medium">{delivery.vehicle_payload_kg} kg</p>
                  </div>
                  <div>
                    <span className="text-slate-600">Remaining Capacity:</span>
                    <p className={`font-medium ${delivery.remaining_capacity_kg > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {delivery.remaining_capacity_kg || 0} kg
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Status:</span>
                    <p className={`font-medium ${
                      delivery.status === 'Completed' ? 'text-green-600' :
                      delivery.status === 'Overweight' ? 'text-red-600' :
                      'text-blue-600'
                    }`}>
                      {delivery.status}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Date:</span>
                    <p className="font-medium">{new Date(delivery.delivery_date).toLocaleDateString('en-GB')}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Supervisor Verification */}
          <Card>
            <CardHeader>
              <CardTitle>Supervisor Verification</CardTitle>
            </CardHeader>
            <CardContent>
              {delivery.supervisor_verified && !showSupervisorVerifyForm ? (
                <div className="space-y-3">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="font-medium text-green-900 mb-2">✓ Verified by Supervisor</p>
                    <p className="text-sm text-green-700">
                      Supervisor: {delivery.supervisor_name}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      Verified at: {new Date(delivery.supervisor_verified_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                  </div>
                  {delivery.supervisor_compliance_statement && (
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-300">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Compliance Statement:</p>
                      <p className="text-sm text-slate-700 leading-relaxed italic">
                        "{delivery.supervisor_compliance_statement}"
                      </p>
                    </div>
                  )}
                  
                  {/* Allow Supervisor/Manager/Admin to re-verify */}
                  {(user.role === 'Supervisor' || user.role === 'Manager' || user.role === 'Admin') && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowSupervisorVerifyForm(true)}
                    >
                      Update Verification
                    </Button>
                  )}
                </div>
              ) : (user.role === 'Supervisor' || user.role === 'Manager' || user.role === 'Admin') || showSupervisorVerifyForm ? (
                // Show verification form for Supervisor, Manager, or Admin
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-900 font-medium mb-3">
                      ⚠️ Supervisor verification required
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id="supervisor-verify"
                          checked={supervisorVerifyConfirmed}
                          onCheckedChange={setSupervisorVerifyConfirmed}
                        />
                        <Label htmlFor="supervisor-verify" className="text-sm leading-relaxed">
                          I confirm that I have verified the recorded pallet weights, checked the total vehicle payload against the vehicle's permitted weight limit, and ensured the load is safe, secure, and compliant with DVSA load-security and weight regulations before releasing the vehicle for departure.
                        </Label>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleSupervisorVerify}
                          disabled={!supervisorVerifyConfirmed}
                          className="flex-1"
                        >
                          Verify & Submit for Driver
                        </Button>
                        {showSupervisorVerifyForm && (
                          <Button 
                            variant="outline"
                            onClick={() => {
                              setShowSupervisorVerifyForm(false);
                              setSupervisorVerifyConfirmed(false);
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-slate-600">
                    Pending supervisor verification
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Driver Acceptance */}
          <Card>
            <CardHeader>
              <CardTitle>Driver Acceptance</CardTitle>
            </CardHeader>
            <CardContent>
              {delivery.driver_accepted ? (
                <div className="space-y-3">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="font-medium text-green-900 mb-2">✓ Delivery Accepted</p>
                    <p className="text-sm text-green-700">
                      Driver: {delivery.driver_name}
                    </p>
                    <p className="text-sm text-green-700">
                      Vehicle: {delivery.vehicle_reg || 'N/A'}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      Accepted at: {new Date(delivery.driver_accepted_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                  </div>
                  {delivery.driver_compliance_statement && (
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-300">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Compliance Statement:</p>
                      <p className="text-sm text-slate-700 leading-relaxed italic">
                        "{delivery.driver_compliance_statement}"
                      </p>
                    </div>
                  )}
                  
                  {/* Driver/Vehicle Reassignment Notice */}
                  {delivery.reassignment_note && (
                    <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-300" data-testid="reassignment-notice">
                      <p className="text-xs font-bold text-blue-800 mb-2 uppercase tracking-wide">
                        ⚠ Driver / Vehicle Change
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-blue-700 whitespace-nowrap">Original:</span>
                          <span className="text-blue-900">
                            {delivery.original_driver_name || 'N/A'} - {delivery.original_vehicle_reg || 'N/A'} 
                            {delivery.original_vehicle_payload && ` (Max Payload: ${delivery.original_vehicle_payload} kg)`}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-blue-700 whitespace-nowrap">Accepted By:</span>
                          <span className="text-blue-900">
                            {delivery.driver_name} - {delivery.vehicle_reg || 'N/A'}
                            {delivery.vehicle_payload_kg && ` (Max Payload: ${delivery.vehicle_payload_kg} kg)`}
                          </span>
                        </div>
                        {delivery.reassigned_at && (
                          <p className="text-xs text-blue-600 mt-2">
                            Reassigned at: {new Date(delivery.reassigned_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : user.role === 'Driver' ? (
                <div className="space-y-4">
                  {delivery.payload_status === 'OVERWEIGHT' && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800 font-medium">
                        ⚠ Cannot accept overweight delivery
                      </p>
                    </div>
                  )}
                  
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-sm text-blue-900">
                      <strong>Signing as:</strong> {user.full_name}
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      {user.email}
                    </div>
                  </div>

                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="driver_confirm"
                      checked={driverConfirmed}
                      onCheckedChange={setDriverConfirmed}
                      disabled={delivery.payload_status === 'OVERWEIGHT'}
                      data-testid="driver-confirm-checkbox"
                    />
                    <Label htmlFor="driver_confirm" className="cursor-pointer text-sm leading-relaxed">
                      I confirm that I have carried out the required vehicle and load checks in accordance with DVSA guidance.
                      I am satisfied that the vehicle is within its permitted weight limits and that the load is secure, correctly distributed, and safe to transport.
                      I accept responsibility for ensuring the vehicle is safe and legal to drive at the time of departure.
                    </Label>
                  </div>

                  <Button
                    onClick={handleDriverAccept}
                    className="w-full"
                    disabled={delivery.payload_status === 'OVERWEIGHT' || !driverConfirmed}
                    data-testid="driver-accept-button"
                  >
                    Accept Delivery & Generate PDF
                  </Button>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-slate-600">
                    {delivery.status === 'Waiting for Driver' 
                      ? 'Awaiting driver acceptance...' 
                      : 'Driver has not yet accepted this delivery'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pallets Table */}
        <Card>
          <CardHeader>
            <CardTitle>Pallets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="pallets-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Pallet #</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Weight (kg)</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Drop #</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Colour</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {pallets.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-slate-500">No pallets added yet</td>
                    </tr>
                  ) : (
                    pallets.map((pallet) => (
                      <tr key={pallet.id} className="border-b border-slate-100">
                        <td className="py-3 px-4">{pallet.pallet_number}</td>
                        <td className="py-3 px-4 font-medium">{pallet.weight_kg}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            pallet.drop_number === 1 ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            Drop {pallet.drop_number}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex w-3 h-3 rounded-full mr-2 ${
                            pallet.colour === 'White' ? 'bg-slate-300 border border-slate-400' :
                            pallet.colour === 'Red' ? 'bg-red-500' :
                            pallet.colour === 'Blue' ? 'bg-blue-500' :
                            'bg-slate-500'
                          }`}></span>
                          {pallet.colour}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{pallet.notes || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Load Photos */}
        <Card>
          <CardHeader>
            <CardTitle>Load Photos</CardTitle>
          </CardHeader>
          <CardContent>
            {photos.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No photos uploaded</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photos-grid">
                {photos.map((photo) => (
                  <a
                    key={photo.id}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files${photo.image_url.replace('/uploads', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative aspect-square rounded-lg overflow-hidden bg-slate-100 hover:ring-2 hover:ring-blue-500 transition-all"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/files${photo.image_url.replace('/uploads', '')}`}
                      alt="Load"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-white" />
                    </div>
                  </a>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
